import { splitAngle } from "@/shared/utils/angle";
import { validateAngle } from "@/shared/utils/angleValidation";
import type {
	InspectionWithValues,
	InspectionValue,
	InspectionReadingPayload,
	UpsertInspectionPayload,
} from "../../models/Inspection";
import {
	formatStoredMeasurement,
	validateMeasurement,
} from "@/shared/utils/measurementPrecision";
import type { SectionSpecItem } from "@/shared/models/SectionSpec";
import { readingKey } from "../../utils/inspectionFormValues";

export { readingKey } from "../../utils/inspectionFormValues";

type DraftReading = Pick<
	InspectionValue,
	"ProfileItemId" | "Code" | "NumericValue" | "Checked"
>;
type FormReading = Omit<DraftReading, "NumericValue"> & {
	NumericValue: string;
};

/** UI-only raw text; domain readings retain their shared numeric ProfileItemId identity. */
import { currentSampleMinute } from "../../utils/sampleTime";

export interface InspectionFormDraft
	extends Pick<UpsertInspectionPayload, "heatId"> {
	sectionId: number | null;
	inspectedAt: Date | null;
	comments: string;
	measuredLbsPerFoot: string;
	readings: Record<
		string,
		Partial<Pick<FormReading, "NumericValue" | "Checked">>
	>;
	angles: Record<string, { degrees: string; minutes: string }>;
}

export const createFormDraft = (
	inspection: InspectionWithValues | null,
	sectionId = inspection?.SectionId ?? null,
): InspectionFormDraft => ({
	sectionId,
	inspectedAt: inspection?.InspectedAt ?? currentSampleMinute(),
	heatId: inspection?.HeatId ?? null,
	comments: inspection?.Comments ?? "",
	measuredLbsPerFoot: formatStoredMeasurement(inspection?.MeasuredLbsPerFoot),
	angles: Object.fromEntries(
		Object.values(inspection?.values ?? {})
			.filter((row) => row.Code === "LA1" || row.Code === "LA2")
			.map((row) => {
				const parts = splitAngle(row.NumericValue);
				return [
					readingKey(row.ProfileItemId),
					{ degrees: parts.Degrees, minutes: parts.Minutes },
				];
			}),
	),
	readings: Object.fromEntries(
		Object.values(inspection?.values ?? {}).map((row) => [
			readingKey(row.ProfileItemId),
			{
				// Angles keep their raw decimal degrees; they validate separately.
				NumericValue:
					row.Code === "LA1" || row.Code === "LA2"
						? row.NumericValue === null
							? ""
							: String(row.NumericValue)
						: formatStoredMeasurement(row.NumericValue),
				Checked: row.Checked,
			},
		]),
	),
});

/** Project onto applicable fields, as M2 does; returning Controllers restore saved defaults. */
export const reconcileFormDraft = (
	draft: InspectionFormDraft,
	items: SectionSpecItem[],
): InspectionFormDraft => {
	const applicable = items.filter((item) => item.Applies);
	return {
		...draft,
		readings: Object.fromEntries(
			applicable.map((item) => {
				const key = readingKey(item.ProfileItemId);
				return [
					key,
					{
						NumericValue: draft.readings[key]?.NumericValue ?? "",
						Checked: draft.readings[key]?.Checked ?? null,
					},
				];
			}),
		),
		angles: Object.fromEntries(
			applicable
				.filter((item) => item.Code === "LA1" || item.Code === "LA2")
				.map((item) => {
					const key = readingKey(item.ProfileItemId);
					return [key, draft.angles[key] ?? { degrees: "", minutes: "" }];
				}),
		),
	};
};

export const getDraftReading = (
	draft: InspectionFormDraft,
	item: SectionSpecItem,
): FormReading => {
	const raw = draft.readings[readingKey(item.ProfileItemId)];
	return {
		ProfileItemId: item.ProfileItemId,
		Code: item.Code,
		NumericValue: raw?.NumericValue ?? "",
		Checked: raw?.Checked ?? null,
	};
};

/** Refuse invalid text, never fall back to the last valid numeric reading. */
export const toInspectionReading = (
	row: FormReading,
	angle?: InspectionFormDraft["angles"][string],
): DraftReading => {
	const validation = angle
		? validateAngle(angle.degrees, angle.minutes)
		: validateMeasurement(row.NumericValue);
	if (validation.message) throw new Error(validation.message);
	return { ...row, NumericValue: validation.value };
};

export const toInspectionReadings = (
	draft: InspectionFormDraft,
	items: SectionSpecItem[],
): InspectionReadingPayload[] =>
	items
		.filter((item) => item.Applies)
		.flatMap((item): InspectionReadingPayload[] => {
			const angle =
				item.Code === "LA1" || item.Code === "LA2"
					? (draft.angles[readingKey(item.ProfileItemId)] ?? {
							degrees: "",
							minutes: "",
						})
					: undefined;
			const row = toInspectionReading(getDraftReading(draft, item), angle);
			if (row.NumericValue !== null && row.Checked !== null)
				throw new Error("A reading cannot be both numeric and checked");
			if (row.NumericValue !== null)
				return [
					{ ProfileItemId: row.ProfileItemId, NumericValue: row.NumericValue },
				];
			if (row.Checked !== null)
				return [{ ProfileItemId: row.ProfileItemId, Checked: row.Checked }];
			return [];
		});
